import {
	ContactType,
	ContactUsage,
	EmailType,
	SalutationType,
	useCreateContactEmailMutation,
	useCreateContactPersonInformationMutation,
	useCreateMailboxContactMutation,
	useUpdateContactEmailMutation,
	useUpdateContactPersonInformationMutation,
	useUpdateMailboxContactMutation,
} from "@repo/server-api";
import { ContactExtended } from "@repo/server-api/types";
import { useTranslation } from "next-i18next";
import React, { useState, useMemo, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Box,
	CircularProgress,
	FormControlLabel,
	Checkbox,
	FormGroup,
	FormLabel,
} from "@mui/material";

export type DialogMailboxProps = {
	open: boolean;
	setOpen: (value: boolean) => void;
	refreshData: (value: ContactExtended) => void;
	mailbox: ContactExtended | null;
	setMailbox: (value: ContactExtended | null) => void;
	companyId: string;
};

export default function DialogMailbox(props: Readonly<DialogMailboxProps>) {
	const { t } = useTranslation();
	const [name, setName] = useState<string>("");
	const [email, setEmail] = useState<string>("");
	const [contactUsage, setContactUsage] = useState<ContactUsage[]>([]);
	const [error, setError] = useState<string>("");

	const [createMailbox, { loading: creating }] = useCreateMailboxContactMutation();
	const [updateMailbox, { loading: updating }] = useUpdateMailboxContactMutation();
	const [createContactEmail, { loading: creatingEmail }] = useCreateContactEmailMutation();
	const [updateContactEmail, { loading: updatingEmail }] = useUpdateContactEmailMutation();
	const [createContactPersonInformation, { loading: creatingInfo }] =
		useCreateContactPersonInformationMutation();
	const [updateContactPersonInformation, { loading: updatingInfo }] =
		useUpdateContactPersonInformationMutation();

	const isLoading = creating || updating || creatingEmail || updatingEmail || creatingInfo || updatingInfo;

	useEffect(() => {
		if (props.open) {
			setName(props.mailbox?.companyName || "");
			// Load email from additionalInformation if editing existing mailbox
			const emailInfos = (props.mailbox?.additionalInformation ?? []).filter(
				(info) => (info as unknown as { __typename?: string }).__typename === "EmailInformation",
			) as unknown as Array<{ emailAddress: string }>;
			const existingEmail = emailInfos.map((e) => e.emailAddress).join(", ") || "";
			setEmail(existingEmail);
			setContactUsage((props.mailbox?.contactUsage as ContactUsage[]) ?? []);
			setError("");
		}
	}, [props.open, props.mailbox]);

	const handleClose = () => {
		props.setOpen(false);
		setError("");
		setName("");
		setEmail("");
		setContactUsage([]);
		props.setMailbox(null);
	};

	const handleUsageChange = (usage: ContactUsage) => {
		setContactUsage((prev) =>
			prev.includes(usage) ? prev.filter((u) => u !== usage) : [...prev, usage],
		);
	};

	// Helper function to get existing email info from additionalInformation
	const getExistingEmailInfo = (): { id: string; emailAddress: string } | null => {
		if (!props.mailbox?.additionalInformation) return null;
		const emailInfo = props.mailbox.additionalInformation.find(
			(info) => (info as unknown as { __typename?: string }).__typename === "EmailInformation",
		) as unknown as { id: string; emailAddress: string } | undefined;
		return emailInfo ?? null;
	};

	// Helper function to handle email create/update - returns updated email info
	const handleEmailUpdate = async (
		mailboxId: string,
	): Promise<{ id: string; emailAddress: string; emailType: EmailType; __typename: string } | null> => {
		const existingEmail = getExistingEmailInfo();
		const newEmailValue = email.trim();

		// No email to set and no existing email - nothing to do
		if (!newEmailValue && !existingEmail) {
			return null;
		}

		// Existing email - update it
		if (existingEmail) {
			if (existingEmail.emailAddress !== newEmailValue && newEmailValue) {
				const result = await updateContactEmail({
					variables: {
						contactId: mailboxId,
						emailId: existingEmail.id,
						emailAddress: newEmailValue,
						emailType: EmailType.Work,
					},
				});
				const updatedEmail = result.data?.updateContactEmail?.emailInformation;
				if (updatedEmail) {
					return {
						id: updatedEmail.id,
						emailAddress: updatedEmail.emailAddress,
						emailType: updatedEmail.emailType,
						__typename: "EmailInformation",
					};
				}
			}
			// Return existing email if no change needed
			return {
				id: existingEmail.id,
				emailAddress: existingEmail.emailAddress,
				emailType: EmailType.Work,
				__typename: "EmailInformation",
			};
		}

		// No existing email but new value provided - create it
		if (newEmailValue) {
			const result = await createContactEmail({
				variables: {
					contactId: mailboxId,
					emailAddress: newEmailValue,
					emailType: EmailType.Work,
				},
			});
			const createdEmail = result.data?.createContactEmail?.emailInformation;
			if (createdEmail) {
				return {
					id: createdEmail.id,
					emailAddress: createdEmail.emailAddress,
					emailType: createdEmail.emailType,
					__typename: "EmailInformation",
				};
			}
		}

		return null;
	};

	// Helper to build updated additionalInformation with new email
	const buildUpdatedAdditionalInformation = (
		existingInfo: ContactExtended["additionalInformation"],
		emailInfo: { id: string; emailAddress: string; emailType: EmailType; __typename: string } | null,
	): ContactExtended["additionalInformation"] => {
		// Filter out old email entries
		const filteredInfo = (existingInfo ?? []).filter(
			(info) => (info as unknown as { __typename?: string }).__typename !== "EmailInformation",
		);

		// Add new email if provided
		if (emailInfo) {
			return [...filteredInfo, emailInfo as unknown as ContactExtended["additionalInformation"][number]];
		}

		return filteredInfo;
	};

	// Helper function to handle ContactPersonInformation create/update
	const handleContactPersonInformation = async (
		mailboxId: string,
		existingInformationId: string | undefined,
	): Promise<string | undefined> => {
		// If no usage selected and no existing info, nothing to do
		if (contactUsage.length === 0 && !existingInformationId) {
			return undefined;
		}

		// If existing informationId, always update (even with empty usage)
		if (existingInformationId) {
			const updateResult = await updateContactPersonInformation({
				variables: {
					id: existingInformationId,
					contactId: props.companyId,
					contactPersonId: mailboxId,
					contactUsage: contactUsage,
				},
			});
			if (updateResult.errors) {
				console.error("Failed to update ContactPersonInformation:", updateResult.errors);
				throw new Error("Failed to update contact usage");
			}
			return existingInformationId;
		}

		// No existing info but usage selected - create new
		if (contactUsage.length > 0) {
			const createResult = await createContactPersonInformation({
				variables: {
					contactId: props.companyId,
					contactPersonId: mailboxId,
					contactUsage: contactUsage,
				},
			});
			if (createResult.errors) {
				console.error("Failed to create ContactPersonInformation:", createResult.errors);
				throw new Error("Failed to create contact usage");
			}
			return createResult.data?.createContactPersonInformation?.contactPersonInformation?.id;
		}

		return undefined;
	};

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		if (!name.trim()) {
			setError(t("name-is-required"));
			return;
		}

		try {
			let mailboxId: string;
			let mailboxData: {
				displayName: string;
				companyName: string;
				contactNumber: string;
				contactTypes: ContactType[];
				additionalInformation?: unknown[];
			};

			// Step 1: Create or update the mailbox contact
			if (props.mailbox?.id) {
				// Update existing mailbox
				const result = await updateMailbox({
					variables: {
						id: props.mailbox.id,
						contactNumber: props.mailbox.contactNumber,
						name: name,
					},
				});
				if (!result.data?.updateMailboxContact?.contact) {
					console.error("Failed to update mailbox:", result.errors);
					setError(t("error"));
					return;
				}
				const contact = result.data.updateMailboxContact.contact;
				mailboxId = props.mailbox.id;
				mailboxData = {
					displayName: contact.displayName,
					companyName: contact.companyName,
					contactNumber: contact.contactNumber,
					contactTypes: contact.contactTypes as ContactType[],
					additionalInformation: props.mailbox.additionalInformation,
				};
			} else {
				// Create new mailbox
				const result = await createMailbox({
					variables: {
						name: name,
						memberOfCompanyId: props.companyId,
					},
				});
				if (!result.data?.createMailboxContact?.contact) {
					console.error("Failed to create mailbox:", result.errors);
					setError(t("error"));
					return;
				}
				const contact = result.data.createMailboxContact.contact;
				mailboxId = contact.id;
				mailboxData = {
					displayName: contact.displayName,
					companyName: contact.companyName,
					contactNumber: contact.contactNumber,
					contactTypes: contact.contactTypes as ContactType[],
					additionalInformation: contact.additionalInformation ?? [],
				};
			}

			// Step 2: Handle email create/update
			const emailInfo = await handleEmailUpdate(mailboxId);

			// Step 3: Handle ContactPersonInformation separately
			const informationId = await handleContactPersonInformation(
				mailboxId,
				props.mailbox?.informationId,
			);

			// Step 4: Build updated additionalInformation with new email
			const updatedAdditionalInfo = buildUpdatedAdditionalInformation(
				props.mailbox?.additionalInformation ?? (mailboxData.additionalInformation as ContactExtended["additionalInformation"]),
				emailInfo,
			);

			// Step 5: Build result and refresh
			const resultMailbox: ContactExtended = props.mailbox?.id
				? {
						...props.mailbox,
						displayName: mailboxData.displayName,
						companyName: mailboxData.companyName,
						contactNumber: mailboxData.contactNumber,
						contactTypes: mailboxData.contactTypes,
						contactUsage: contactUsage,
						informationId: informationId,
						additionalInformation: updatedAdditionalInfo,
					}
				: {
						id: mailboxId,
						displayName: mailboxData.displayName,
						companyName: mailboxData.companyName,
						contactNumber: mailboxData.contactNumber,
						contactTypes: mailboxData.contactTypes,
						displayContactType: mailboxData.contactTypes,
						firstName: "",
						lastName: "",
						salutation: SalutationType.Mr,
						title: "",
						shortCode: "",
						additionalInformation: updatedAdditionalInfo,
						isMemberOf: [props.companyId],
						isACompany: false,
						isAFreelancer: false,
						isAPerson: false,
						isAMailbox: true,
						isContactPerson: false,
						isEmployee: false,
						isDeleted: false,
						isTemporary: false,
						version: 0,
						contactUsage: contactUsage,
						informationId: informationId,
					};

			props.refreshData(resultMailbox);
			handleClose();
		} catch (err) {
			console.error("Error in handleSubmit:", err);
			setError(t("error"));
		}
	};

	const title = useMemo(() => {
		return props.mailbox?.id ? t("mailbox.edit") : t("mailbox.create");
	}, [props.mailbox?.id, t]);

	return (
		<Dialog open={props.open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="mailbox-dialog">
			<form onSubmit={handleSubmit}>
				<DialogTitle>{title}</DialogTitle>
				<DialogContent>
					<Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
						<TextField
							fullWidth
							label={t("mailbox.name")}
							value={name}
							onChange={(e) => {
								setName(e.target.value);
								if (error) setError("");
							}}
							error={!!error}
							helperText={error}
							placeholder={t("mailbox.name-placeholder")}
							disabled={isLoading}
							autoFocus
							data-testid="mailbox-name-input"
						/>
						<TextField
							fullWidth
							label={t("mailbox.email")}
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder={t("mailbox.email-placeholder")}
							disabled={isLoading}
							data-testid="mailbox-email-input"
						/>
						<Box data-testid="mailbox-usage-options">
							<FormLabel component="legend">{t("mailbox.usage-options")}</FormLabel>
							<FormGroup row>
								<FormControlLabel
									control={
										<Checkbox
											checked={contactUsage.includes(ContactUsage.Invoice)}
											onChange={() => handleUsageChange(ContactUsage.Invoice)}
											disabled={isLoading}
											data-testid="mailbox-usage-invoice"
										/>
									}
									label={t("contact-usage.invoice")}
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={contactUsage.includes(ContactUsage.InvoiceReminder)}
											onChange={() => handleUsageChange(ContactUsage.InvoiceReminder)}
											disabled={isLoading}
											data-testid="mailbox-usage-invoice-reminder"
										/>
									}
									label={t("contact-usage.invoice-reminder")}
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={contactUsage.includes(ContactUsage.Offer)}
											onChange={() => handleUsageChange(ContactUsage.Offer)}
											disabled={isLoading}
											data-testid="mailbox-usage-offer"
										/>
									}
									label={t("contact-usage.offer")}
								/>
							</FormGroup>
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} disabled={isLoading} data-testid="mailbox-cancel-button">
						{t("cancel")}
					</Button>
					<Button
						type="submit"
						variant="contained"
						color="primary"
						disabled={isLoading}
						startIcon={isLoading ? <CircularProgress size={20} /> : null}
						data-testid="mailbox-save-button">
						{t("save")}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}
